import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, formatDateTime, buildSistemaWhatsappMessage, openWhatsapp } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, MessageCircle, FileEdit, Trash2, MapPin, Phone, ImageOff, Check } from "lucide-react";

const STATUSES = [
  { key: "todas", label: "Todas" },
  { key: "nao_lidas", label: "Não lidas" },
  { key: "nova", label: "Novas" },
  { key: "orcamento_gerado", label: "Com orçamento" },
  { key: "descartada", label: "Descartadas" },
];

const STATUS_STYLE = {
  nova: "bg-emerald-100 text-emerald-800",
  orcamento_gerado: "bg-blue-100 text-blue-800",
  descartada: "bg-zinc-100 text-zinc-500",
};

export default function Solicitacoes() {
  const [items, setItems] = useState([]);
  const [filtro, setFiltro] = useState("todas");
  const [carregando, setCarregando] = useState(true);
  const nav = useNavigate();

  const carregar = () => {
    setCarregando(true);
    const params = {};
    if (filtro === "nao_lidas") params.lida = false;
    else if (filtro !== "todas") params.status = filtro;
    api.get("/solicitacoes", { params })
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Erro ao carregar solicitações"))
      .finally(() => setCarregando(false));
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filtro]);

  const gerarOrcamento = async (s) => {
    if (!window.confirm(`Gerar orçamento a partir desta solicitação? Ela será marcada como "orçamento gerado".`)) return;
    try {
      const { data } = await api.post(`/solicitacoes/${s.id}/gerar-orcamento`);
      toast.success(`Orçamento ${data.numero_orcamento} criado`);
      nav(`/admin/orcamentos/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar orçamento");
    }
  };

  const conversarWhatsapp = (s) => {
    const nomeC = (s.cliente?.nome || "cliente").trim();
    const msg = buildSistemaWhatsappMessage([
      `Olá ${nomeC}, recebemos sua solicitação de orçamento com os seguintes serviços:`,
      "",
      ...(s.itens || []).map((it, idx) => `• ${idx + 1}. ${it.descricao}`),
      "",
      `Endereço: ${s.endereco}`,
      "",
      "Podemos agendar uma conversa para alinhar os detalhes?",
    ]);
    openWhatsapp(s.cliente?.whatsapp || s.cliente?.telefone, msg);
  };

  const descartar = async (s) => {
    if (!window.confirm(`Descartar solicitação de ${s.cliente?.nome || "cliente"}?`)) return;
    try {
      await api.post(`/solicitacoes/${s.id}/descartar`);
      toast.success("Solicitação descartada");
      carregar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const marcarLida = async (s) => {
    try {
      await api.post(`/solicitacoes/${s.id}/marcar-lida`);
      // Atualiza localmente pra evitar refetch (mais responsivo)
      setItems((arr) => arr.map((x) => (x.id === s.id ? { ...x, lida: true } : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const marcarTodasLidas = async () => {
    try {
      const { data } = await api.post(`/solicitacoes/marcar-todas-lidas`);
      toast.success(`${data.atualizadas} marcada(s) como lida(s)`);
      carregar();
    } catch (e) { toast.error("Erro ao marcar todas"); }
  };

  const excluir = async (s) => {
    if (!window.confirm(`Excluir permanentemente esta solicitação?`)) return;
    try {
      await api.delete(`/solicitacoes/${s.id}`);
      toast.success("Solicitação excluída");
      carregar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6" data-testid="solicitacoes-page">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">Recebidas</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Solicitações</h1>
        <p className="text-sm text-zinc-600 mt-1">
          Pedidos de orçamento enviados pelos clientes através do portal.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {STATUSES.map((s) => (
            <button
              key={s.key}
              onClick={() => setFiltro(s.key)}
              data-testid={`filter-${s.key}`}
              className={`px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wide ${
                filtro === s.key ? "bg-zinc-900 text-white" : "bg-white border border-zinc-200 text-zinc-600"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button
          onClick={marcarTodasLidas}
          data-testid="marcar-todas-lidas-btn"
          className="ml-auto px-3 py-2 rounded-md text-xs font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
        >
          Marcar todas como lidas
        </button>
      </div>

      {carregando ? (
        <div className="text-sm text-zinc-500">Carregando…</div>
      ) : items.length === 0 ? (
        <div className="bbh-card p-10 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-zinc-100 flex items-center justify-center mb-3">
            <Inbox className="w-6 h-6 text-zinc-500" />
          </div>
          <div className="text-sm text-zinc-500">Nenhuma solicitação neste filtro.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {items.map((s) => (
            <SolicitacaoCard
              key={s.id}
              solicitacao={s}
              onGerar={() => gerarOrcamento(s)}
              onWhatsapp={() => conversarWhatsapp(s)}
              onDescartar={() => descartar(s)}
              onExcluir={() => excluir(s)}
              onMarcarLida={() => marcarLida(s)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SolicitacaoCard({ solicitacao: s, onGerar, onWhatsapp, onDescartar, onExcluir, onMarcarLida }) {
  const podeGerar = s.status === "nova";
  const naoLida = s.lida !== true;
  return (
    <div
      className={`bbh-card p-4 flex flex-col relative ${naoLida ? "ring-2 ring-rose-200" : ""}`}
      data-testid={`solicitacao-${s.id}`}
    >
      {naoLida && (
        <span
          className="absolute -top-2 -left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold uppercase tracking-wide shadow"
          data-testid={`nao-lida-${s.id}`}
        >
          Nova
        </span>
      )}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="font-heading font-bold text-base truncate">{s.cliente?.nome || "-"}</div>
          <div className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5">
            <Phone className="w-3 h-3" />
            {s.cliente?.whatsapp || s.cliente?.telefone || s.cliente?.email || "sem contato"}
          </div>
        </div>
        <span className={`px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${STATUS_STYLE[s.status] || "bg-zinc-100 text-zinc-700"}`}>
          {s.status === "orcamento_gerado" ? "Orçamento gerado" : s.status}
        </span>
      </div>

      <div className="text-xs text-zinc-500 mb-3">
        Recebida em {formatDateTime(s.criado_em)}
      </div>

      <div className="bg-zinc-50 rounded-md p-3 mb-3">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">
          <MapPin className="w-3 h-3" /> Endereço · {s.endereco_apelido || "-"}
        </div>
        <div className="text-sm text-zinc-800">{s.endereco || "-"}</div>
      </div>

      <div className="space-y-2 mb-3">
        {(s.itens || []).map((it, idx) => (
          <div key={idx} className="flex gap-3 items-start">
            {it.foto ? (
              <a
                href={fileUrl(it.foto)}
                target="_blank"
                rel="noreferrer"
                className="shrink-0"
              >
                <img
                  src={fileUrl(it.foto)}
                  alt=""
                  className="w-16 h-16 object-cover rounded-md border border-zinc-200"
                />
              </a>
            ) : (
              <div className="shrink-0 w-16 h-16 rounded-md border border-dashed border-zinc-200 flex items-center justify-center text-zinc-300">
                <ImageOff className="w-5 h-5" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Serviço {idx + 1}</div>
              <div className="text-sm text-zinc-800 whitespace-pre-wrap">{it.descricao || "-"}</div>
            </div>
          </div>
        ))}
      </div>

      {s.observacao && (
        <div className="mb-3 text-xs bg-amber-50 border-l-2 border-amber-300 p-2 text-zinc-700">
          <span className="font-semibold">Observação: </span>{s.observacao}
        </div>
      )}

      <div className="mt-auto pt-3 border-t border-zinc-100 flex flex-wrap gap-2">
        {podeGerar && (
          <button
            onClick={onGerar}
            data-testid={`gerar-orcamento-${s.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bbh-btn-primary text-xs font-semibold"
          >
            <FileEdit className="w-3.5 h-3.5" /> Gerar orçamento
          </button>
        )}
        <button
          onClick={onWhatsapp}
          data-testid={`whatsapp-solicitacao-${s.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
        >
          <MessageCircle className="w-3.5 h-3.5" /> Conversar no WhatsApp
        </button>
        {podeGerar && (
          <button
            onClick={onDescartar}
            data-testid={`descartar-${s.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-200 text-zinc-700 text-xs font-semibold hover:bg-zinc-50"
          >
            Descartar
          </button>
        )}
        {naoLida && (
          <button
            onClick={onMarcarLida}
            data-testid={`marcar-lida-${s.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-200 text-zinc-700 text-xs font-semibold hover:bg-zinc-50"
            title="Marcar como lida"
          >
            <Check className="w-3.5 h-3.5" /> Marcar como lida
          </button>
        )}
        <button
          onClick={onExcluir}
          data-testid={`excluir-${s.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-rose-600 hover:bg-rose-50 text-xs font-semibold ml-auto"
          title="Excluir"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
